import { useRef, useState, useEffect } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../services/firebase';
import { useImpersonation } from '../../hooks/useImpersonation';
import { SupportChatMessage } from './SupportChatMessage';
import { OpenTicketsPanel } from './OpenTicketsPanel';
import { useOpenTickets } from './useOpenTickets';

interface Message {
  role: 'user' | 'assistant';
  content: string;
  ts: string;
}

interface SupportChatResponse {
  status: 'ok' | 'locked' | 'paused' | 'conversation_full';
  conversationId?: string;
  assistantText?: string;
  ticketOpened?: string | null;
  messagesRemainingToday?: number;
  message?: string;
}

interface Props {
  onClose: () => void;
}

export function SupportChat({ onClose }: Props) {
  const [messages, setMessages] = useState<Message[]>([
    {
      role: 'assistant',
      content:
        "Hi — I'm the JAD Support Assistant. Ask me anything about your account, your credits, your recent calls, or uploads, and I'll take a look.",
      ts: new Date().toISOString(),
    },
  ]);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [remaining, setRemaining] = useState<number | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const { tickets } = useOpenTickets();
  const { isImpersonating, impersonatedClientId, impersonatedClientName } =
    useImpersonation();

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages]);

  const supportChatFn = httpsCallable<
    { conversationId: string | null; message: string; clientId?: string },
    SupportChatResponse
  // The function allows 300s per turn; the Firebase default client timeout is
  // 70s, which showed an error while the server was still answering.
  >(functions, 'supportChat', { timeout: 310_000 });

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;

    setInput('');
    setBusy(true);

    const userMsg: Message = { role: 'user', content: text, ts: new Date().toISOString() };
    setMessages(prev => [...prev, userMsg]);

    try {
      const { data } = await supportChatFn({
        conversationId,
        message: text,
        // When an admin is impersonating a client, query THAT client's data
        // (matches the pattern used by every other callable invocation in Dashboard.tsx).
        ...(isImpersonating && impersonatedClientId
          ? { clientId: impersonatedClientId }
          : {}),
      });

      if (data.status === 'locked' || data.status === 'paused' || data.status === 'conversation_full') {
        setMessages(prev => [
          ...prev,
          {
            role: 'assistant',
            content: data.message ?? 'Unable to respond right now.',
            ts: new Date().toISOString(),
          },
        ]);
        return;
      }

      if (data.conversationId && !conversationId) {
        setConversationId(data.conversationId);
      }
      if (typeof data.messagesRemainingToday === 'number') {
        setRemaining(data.messagesRemainingToday);
      }
      setMessages(prev => [
        ...prev,
        {
          role: 'assistant',
          // Not `??`: an empty string must never render as a blank bubble.
          content: data.assistantText?.trim()
            ? data.assistantText
            : "I wasn't able to answer that one. Please try asking it a different way.",
          ts: new Date().toISOString(),
        },
      ]);
    } catch (e) {
      // Log the raw error for dev console / Sentry, but do NOT surface codes like
      // "INTERNAL" / "UNAUTHENTICATED" to the user — the friendly assistant message
      // below is the only thing they should see.
      console.error('[SupportChat] call failed:', e);
      setMessages(prev => [
        ...prev,
        {
          role: 'assistant',
          content:
            "I ran into a problem reaching the support system. Please try again in a moment, or email info@jadenterprises.org directly.",
          ts: new Date().toISOString(),
        },
      ]);
    } finally {
      setBusy(false);
    }
  };

  const handleKey = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <div className="chat-modal-overlay" onClick={onClose}>
      <div className="chat-modal" onClick={e => e.stopPropagation()}>
        <div className="chat-modal-header">
          <h3>JAD Support Assistant</h3>
          <div className="chat-modal-header-right">
            {remaining !== null && (
              <span className="chat-modal-usage">{remaining} messages remaining today</span>
            )}
            <button onClick={onClose} className="chat-modal-close" aria-label="Close chat">
              ×
            </button>
          </div>
        </div>

        {isImpersonating && impersonatedClientId && (
          <div
            style={{
              padding: '8px 20px',
              background: 'rgba(221, 177, 84, 0.12)',
              borderBottom: '1px solid rgba(221, 177, 84, 0.35)',
              fontSize: '0.85rem',
              color: 'var(--gold-light, #e6c574)',
            }}
          >
            Viewing as client: <b>{impersonatedClientName ?? impersonatedClientId}</b>
          </div>
        )}

        <OpenTicketsPanel tickets={tickets} />

        <div className="chat-msg-list" ref={scrollRef}>
          {messages.map((m, i) => (
            <SupportChatMessage key={i} role={m.role} content={m.content} />
          ))}
          {busy && (
            <div className="chat-msg chat-msg-assistant">
              <div className="chat-msg-bubble chat-msg-typing">Thinking…</div>
            </div>
          )}
        </div>


        <div className="chat-modal-input-row">
          <textarea
            className="chat-modal-input"
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKey}
            placeholder="Ask about your account, credits, uploads, or recent calls…"
            rows={2}
            disabled={busy}
          />
          <button
            onClick={send}
            disabled={busy || !input.trim()}
            className="btn btn-primary chat-modal-send"
          >
            Send
          </button>
        </div>
      </div>
    </div>
  );
}
